import { useEffect, useMemo, useRef, useState } from "react";
import debounce from "lodash.debounce";
import { ISearchRequestParamsOmitCountry } from "src/components/pages/search/search.types";
import { ISearchStore, useSearchStore } from "src/components/pages/search/store/search-store";
import { getMappedStore } from "src/components/pages/search/store/utils";

const POPOVER_FILTER_FIELDS_DEBOUNCE_MS = 300;

interface IPopoverFilterFields {
    tags: ISearchStore["tags"];
    brokersIds: ISearchStore["brokersIds"];
    keyFigures: ISearchStore["keyFigures"];
}

const usePopoverFilterFieldsDebounced = (
    tags: ISearchStore["tags"],
    brokersIds: ISearchStore["brokersIds"],
    keyFigures: ISearchStore["keyFigures"]
): IPopoverFilterFields => {
    const [debouncedFields, setDebouncedFields] = useState<IPopoverFilterFields>({ tags, brokersIds, keyFigures });
    const debouncedSetFields = useMemo(() => debounce(setDebouncedFields, POPOVER_FILTER_FIELDS_DEBOUNCE_MS, { leading: true, trailing: true }), []);
    const lastDispatchedRef = useRef<IPopoverFilterFields>({ tags, brokersIds, keyFigures });

    useEffect(() => {
        const last = lastDispatchedRef.current;
        if (tags === last.tags && brokersIds === last.brokersIds && keyFigures === last.keyFigures) {
            return;
        }
        lastDispatchedRef.current = { tags, brokersIds, keyFigures };
        debouncedSetFields({ tags, brokersIds, keyFigures });
    }, [tags, brokersIds, keyFigures, debouncedSetFields]);

    useEffect(() => () => debouncedSetFields.cancel(), [debouncedSetFields]);

    return debouncedFields;
};

export const useSearchRequestParams = (): ISearchRequestParamsOmitCountry | null => {
    const state = useSearchStore();
    const debouncedFilterFields = usePopoverFilterFieldsDebounced(state.tags, state.brokersIds, state.keyFigures);

    return useMemo(() => {
        if (!state.hasHydratedFromUrl) {
            return null;
        }

        return getMappedStore({ ...state, ...debouncedFilterFields });
    }, [state, debouncedFilterFields]);
};
